'use client';

import { useEffect, useState } from 'react';
import Script from 'next/script';
import { Link } from '@/i18n/routing';
import { getBikeparks } from '@/lib/api';
import { Bikepark } from '@/types';
import { useTranslations, useLocale } from 'next-intl';

export default function BikeparksRankingPage() {
  const t = useTranslations('ranking');
  const tCommon = useTranslations('common');
  const locale = useLocale();

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Start",
        "item": `https://bikeparkmapa.pl${locale === 'pl' ? '' : `/${locale}`}`
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Bikeparki",
        "item": `https://bikeparkmapa.pl${locale === 'pl' ? '' : `/${locale}`}/bikeparki`
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": "Ranking",
        "item": `https://bikeparkmapa.pl${locale === 'pl' ? '' : `/${locale}`}/bikeparki/ranking`
      }
    ]
  };

  const [bikeparks, setBikeparks] = useState<Bikepark[]>([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState<string>('overall');
  const [descriptionExpanded, setDescriptionExpanded] = useState(false);

  useEffect(() => {
    async function loadBikeparks() {
      const data = await getBikeparks(locale);
      setBikeparks(data);
      setLoading(false);
    }
    loadBikeparks();
  }, [locale]);

  const getRankedParks = () => {
    switch (category) {
      case 'overall':
        return [...bikeparks].sort((a, b) => b.rating - a.rating);
      case 'price-best':
        return [...bikeparks]
          .filter(p => p.price && p.price > 0)
          .sort((a, b) => (b.rating / (b.price || 1)) - (a.rating / (a.price || 1)));
      case 'most-trails':
        return [...bikeparks]
          .filter(p => p.trails && p.trails.length > 0)
          .sort((a, b) => (b.trails?.length || 0) - (a.trails?.length || 0));
      case 'free':
        return [...bikeparks]
          .filter(p => p.price === 0)
          .sort((a, b) => b.rating - a.rating);
      default:
        return bikeparks;
    }
  };

  const rankedParks = getRankedParks();

  if (loading) {
    return (
      <div className="ranking-page">
        <div style={{ padding: '100px 20px', textAlign: 'center' }}>
          <h2>{tCommon('loading')}...</h2>
        </div>
      </div>
    );
  }

  return (
    <div className="ranking-page">
      <Script
        id="breadcrumb-schema"
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />
      <header className="ranking-header">
        <div className="ranking-header-content">
          <h1>🏆 {t('titleFull')}</h1>
          <div className="ranking-description">
            <p>{t('description1')}</p>
            {descriptionExpanded && (
              <>
                <p>{t('description2')}</p>
                <p>{t('description3')}</p>
              </>
            )}
            <button
              className="read-more-btn"
              onClick={() => setDescriptionExpanded(!descriptionExpanded)}
            >
              {descriptionExpanded ? `▲ ${t('collapse')}` : `▼ ${t('readMore')}`}
            </button>
          </div>
          <div className="ranking-nav">
            <Link href="/bikeparki" className="btn-secondary">🗺️ {tCommon('map')}</Link>
            <Link href="/bikeparki/lista" className="btn-secondary">📋 {tCommon('list')}</Link>
            <Link href="/" className="btn-secondary">🏠 {tCommon('home')}</Link>
          </div>
        </div>
      </header>

      <div className="ranking-container">
        <div className="ranking-categories">
          <button
            className={`category-btn ${category === 'overall' ? 'active' : ''}`}
            onClick={() => setCategory('overall')}
          >
            🏆 {t('categoryOverall')}
          </button>
          <button
            className={`category-btn ${category === 'price-best' ? 'active' : ''}`}
            onClick={() => setCategory('price-best')}
          >
            💎 {t('categoryPriceValue')}
          </button>
          <button
            className={`category-btn ${category === 'most-trails' ? 'active' : ''}`}
            onClick={() => setCategory('most-trails')}
          >
            🛤️ {t('categoryMostTrails')}
          </button>
          <button
            className={`category-btn ${category === 'free' ? 'active' : ''}`}
            onClick={() => setCategory('free')}
          >
            💚 {t('categoryBestFree')}
          </button>
        </div>

        <div className="ranking-list">
          {rankedParks.slice(0, 50).map((park, index) => (
            <article key={park.documentId} className={`ranking-item ${index < 3 ? `rank-${index + 1}` : ''}`}>
              <div className="rank-number">
                {index + 1}
              </div>
              <div className="rank-content">
                <div className="rank-header">
                  <h3>{park.name}</h3>
                  <div className="rank-rating">
                    {'★'.repeat(park.rating)}{'☆'.repeat(5 - park.rating)}
                  </div>
                </div>
                <div className="rank-meta">
                  <span>📍 {park.region}</span>
                  {park.trails && park.trails.length > 0 && (
                    <span>🛤️ {park.trails.length} {t('trails')}</span>
                  )}
                  {park.price !== undefined && park.price !== null && (
                    <span>{park.price === 0 ? `💚 ${t('free')}` : `💰 ${park.price} zł`}</span>
                  )}
                </div>
                {park.blurb && (
                  <p className="rank-blurb">{park.blurb}</p>
                )}
              </div>
              <div className="rank-actions">
                <Link href={`/bikepark/${park.slug}`} className="btn-view">
                  {t('view')} →
                </Link>
              </div>
            </article>
          ))}
        </div>
      </div>

      <style jsx>{`
        .ranking-page {
          min-height: 100vh;
          background: var(--bg);
          color: var(--text);
        }

        .ranking-header {
          background: linear-gradient(135deg, var(--panel), var(--bg));
          padding: 40px 20px;
          border-bottom: 1px solid var(--border);
        }

        .ranking-header-content {
          max-width: 1200px;
          margin: 0 auto;
        }

        .ranking-header h1 {
          font-size: clamp(28px, 5vw, 40px);
          margin: 0 0 12px;
        }

        .ranking-header p {
          font-size: 16px;
          color: var(--muted);
          margin: 0 0 20px;
        }

        .ranking-description {
          max-width: 900px;
          margin-bottom: 24px;
        }

        .ranking-description p {
          font-size: 15px;
          line-height: 1.7;
          color: var(--text-secondary);
          margin-bottom: 16px;
        }

        .ranking-description p:last-child {
          margin-bottom: 0;
        }

        .read-more-btn {
          background: none;
          border: none;
          color: var(--primary);
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          padding: 8px 0;
          margin-top: 12px;
          transition: all 0.2s;
          display: inline-flex;
          align-items: center;
          gap: 4px;
        }

        .read-more-btn:hover {
          color: var(--primary-hover);
          transform: translateY(-1px);
        }

        .ranking-nav {
          display: flex;
          gap: 12px;
          flex-wrap: wrap;
        }

        .ranking-container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 24px 20px;
        }

        .ranking-categories {
          display: flex;
          gap: 12px;
          margin-bottom: 32px;
          overflow-x: auto;
          padding-bottom: 12px;
        }

        .category-btn {
          padding: 12px 20px;
          border-radius: 12px;
          border: 1px solid var(--border);
          background: var(--panel);
          color: var(--text);
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
          white-space: nowrap;
        }

        .category-btn:hover {
          background: var(--accent);
          border-color: var(--accent);
          color: #fff;
        }

        .category-btn.active {
          background: linear-gradient(135deg, #667eea, #764ba2);
          border-color: transparent;
          color: #fff;
        }

        .ranking-list {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }

        .ranking-item {
          display: flex;
          gap: 20px;
          background: var(--panel);
          border: 1px solid var(--border);
          border-radius: 16px;
          padding: 20px;
          transition: all 0.3s;
        }

        .ranking-item:hover {
          transform: translateX(4px);
          box-shadow: 0 8px 24px rgba(0, 0, 0, 0.3);
          border-color: var(--accent);
        }

        .rank-1 {
          background: linear-gradient(135deg, rgba(255, 215, 0, 0.1), rgba(255, 193, 7, 0.05));
          border-color: rgba(255, 215, 0, 0.3);
        }

        .rank-2 {
          background: linear-gradient(135deg, rgba(192, 192, 192, 0.1), rgba(169, 169, 169, 0.05));
          border-color: rgba(192, 192, 192, 0.3);
        }

        .rank-3 {
          background: linear-gradient(135deg, rgba(205, 127, 50, 0.1), rgba(184, 115, 51, 0.05));
          border-color: rgba(205, 127, 50, 0.3);
        }

        .rank-number {
          font-size: 32px;
          font-weight: 700;
          color: var(--accent);
          width: 60px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .rank-1 .rank-number {
          color: #FFD700;
          font-size: 40px;
        }

        .rank-2 .rank-number {
          color: #C0C0C0;
          font-size: 36px;
        }

        .rank-3 .rank-number {
          color: #CD7F32;
          font-size: 36px;
        }

        .rank-content {
          flex: 1;
          min-width: 0;
        }

        .rank-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          gap: 12px;
          margin-bottom: 8px;
        }

        .rank-header h3 {
          font-size: 20px;
          margin: 0;
          flex: 1;
        }

        .rank-rating {
          color: #ffd24a;
          font-size: 16px;
          white-space: nowrap;
        }

        .rank-meta {
          display: flex;
          gap: 16px;
          font-size: 13px;
          color: var(--muted);
          margin-bottom: 8px;
          flex-wrap: wrap;
        }

        .rank-blurb {
          font-size: 14px;
          line-height: 1.5;
          color: var(--muted);
          margin: 0;
        }

        .rank-actions {
          display: flex;
          align-items: center;
        }

        .btn-view {
          padding: 10px 20px;
          border-radius: 8px;
          background: var(--accent);
          color: #fff;
          text-decoration: none;
          font-size: 14px;
          font-weight: 500;
          transition: all 0.2s;
          white-space: nowrap;
        }

        .btn-view:hover {
          filter: brightness(1.1);
          transform: translateX(2px);
        }

        .btn-secondary {
          display: inline-block;
          padding: 8px 16px;
          border-radius: 8px;
          text-decoration: none;
          font-size: 14px;
          font-weight: 500;
          transition: all 0.2s;
          background: var(--panel);
          border: 1px solid var(--border);
          color: var(--text);
        }

        .btn-secondary:hover {
          background: var(--accent);
          color: #fff;
        }

        @media (max-width: 768px) {
          .ranking-item {
            flex-direction: column;
            gap: 16px;
          }

          .rank-number {
            width: auto;
            font-size: 28px;
          }

          .rank-1 .rank-number {
            font-size: 32px;
          }

          .rank-actions {
            justify-content: stretch;
          }

          .btn-view {
            width: 100%;
            text-align: center;
          }
        }
      `}</style>
    </div>
  );
}
